import type { User } from "firebase/auth";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type Timestamp,
  type Unsubscribe
} from "firebase/firestore";
import type { AppNotification, NotificationType } from "../types/imobi";
import { db } from "./firebase";

function serializeTimestamp(value: unknown): string {
  if (value && typeof value === "object" && "toDate" in value) {
    return (value as Timestamp).toDate().toISOString();
  }

  return typeof value === "string" ? value : "";
}

function mapNotification(companyId: string, id: string, data: Record<string, unknown>): AppNotification {
  return {
    id,
    companyId,
    recipientUserId: String(data.recipientUserId ?? ""),
    type: (data.type ?? "return_started_holder") as NotificationType,
    title: String(data.title ?? ""),
    body: String(data.body ?? ""),
    read: data.read === true,
    readAt: serializeTimestamp(data.readAt) || undefined,
    externalServiceId: data.externalServiceId ? String(data.externalServiceId) : undefined,
    externalServiceItemId: data.externalServiceItemId ? String(data.externalServiceItemId) : undefined,
    assetId: data.assetId ? String(data.assetId) : undefined,
    createdAt: serializeTimestamp(data.createdAt),
    createdBy: String(data.createdBy ?? "")
  };
}

export function subscribeMyNotifications(companyIds: string[], uid: string, onChange: (notifications: AppNotification[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db || !companyIds.length || !uid) {
    onChange([]);
    return () => undefined;
  }

  const notificationsByCompany = new Map<string, AppNotification[]>();
  const emit = () => {
    onChange(Array.from(notificationsByCompany.values()).flat().sort((left, right) => right.createdAt.localeCompare(left.createdAt)));
  };

  const unsubscribes = companyIds.map((companyId) => onSnapshot(
    query(
      collection(db!, "companies", companyId, "notifications"),
      where("recipientUserId", "==", uid),
      orderBy("createdAt", "desc")
    ),
    (snapshot) => {
      notificationsByCompany.set(companyId, snapshot.docs.map((notificationDoc) => mapNotification(companyId, notificationDoc.id, notificationDoc.data())));
      emit();
    },
    (error) => onError(error.message)
  ));

  return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
}

export async function markNotificationRead(companyId: string, notificationId: string, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  await updateDoc(doc(db, "companies", companyId, "notifications", notificationId), {
    read: true,
    readAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}
